import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';

// Mock data removed - will load from API when available

const MemberTaskDetails = () => {
  const [taskList, setTaskList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Force component to mount and log
  console.log('MemberTaskDetails component mounted');
  console.log('Current URL:', window.location.href);
  console.log('Search string:', window.location.search);

  // Get parameters directly from window.location.search as fallback
  const urlParams = new URLSearchParams(window.location.search);
  
  // Get parameters from URL - try both methods
  let userId = searchParams.get('userId') || urlParams.get('userId');
  let status = searchParams.get('Status') || urlParams.get('Status'); 
  let userType = searchParams.get('userType') || urlParams.get('userType');
  let startDate = searchParams.get('startDate') || urlParams.get('startDate');
  let endDate = searchParams.get('endDate') || urlParams.get('endDate');
  let externalUserId = searchParams.get('externalUserId') || urlParams.get('externalUserId');
  let loggedUserName = searchParams.get('loggedUserName') || urlParams.get('loggedUserName');

  console.log('Extracted parameters:', {
    userId,
    status,
    userType,
    startDate,
    endDate,
    externalUserId,
    loggedUserName
  });

  useEffect(() => {
    console.log('🔥 MemberTaskDetails useEffect running!');
    console.log('Parameters for API call:', {
      userId,
      status,
      userType,
      startDate,
      endDate,
      externalUserId,
      loggedUserName
    });
    
    const loadTaskDetails = async () => {
      try {
        console.log('🚀 Starting API call to /api/Task/userwise');
        setLoading(true);
        
        // API call to load task details using the userwise endpoint
        const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5199';
        
        const params = new URLSearchParams({
          CaseManagerOrQueueId: externalUserId || '',
          DueDateFrom: startDate || '',
          DueDateTo: endDate || '',
          UserType: userType || '',
          UserID: userId || '',
          Status: status || ''
        }).toString();
        
        const apiUrl = `${API_BASE_URL}/api/Task/userwise?${params}`;
        console.log('🎯 API URL being called:', apiUrl);
        
        console.log('📡 Making fetch request...');
        const response = await fetch(apiUrl);
        console.log('📡 Response received:', response.status, response.statusText);
        
        if (!response.ok) {
          throw new Error(`Failed to fetch task details: ${response.status} ${response.statusText}`);
        }
        
        const data = await response.json();
        console.log('✅ API Response data:', data);
        
        // Extract taskStatusList from the response - API returns nested structure
        let taskList = [];
        if (data && data.taskStatusList && Array.isArray(data.taskStatusList)) {
          taskList = data.taskStatusList;
        } else if (Array.isArray(data)) {
          taskList = data;
        }
        
        console.log('📋 Extracted task list:', taskList);
        console.log('📋 Task list length:', taskList.length);
        
        setTaskList(taskList);
        
      } catch (err) {
        console.error('❌ Error loading task details:', err);
        setError(err.message);
      } finally {
        console.log('🏁 API call completed, setting loading to false');
        setLoading(false);
      }
    };

    // Call API directly without conditions
    loadTaskDetails();
  }, []);

  const handleBackClick = () => {
    // Get original parameters to navigate back to chart
    const urlParams = new URLSearchParams(window.location.search);
    const backParams = new URLSearchParams();
    
    // Preserve original parameters for navigation back to chart
    const externalUserId = urlParams.get('externalUserId');
    const userTypeParam = urlParams.get('userType');
    const startDateParam = urlParams.get('startDate');
    const endDateParam = urlParams.get('endDate');
    
    if (externalUserId) backParams.set('externalUserId', externalUserId);
    if (userTypeParam) backParams.set('userType', userTypeParam);
    if (startDateParam) backParams.set('startDate', startDateParam);
    if (endDateParam) backParams.set('endDate', endDateParam);
    
    // Navigate back to the main chart page by updating URL
    window.history.pushState({}, '', `?${backParams.toString()}`);
    // This will trigger the parent component to show chart instead of details
    window.location.reload();
  };

  const handleTaskClick = (taskId, taskSubject) => {
    // Handle opening case in parent window (similar to original postMessage)
    const data = {
      eventName: 'cca.navigate',
      page: 'ManageAssignments',
      options: {
        tab: 'tasks',
        targetId: userId,
        columns: [
          { name: 'first_name' },
          { name: 'last_name' },
          { name: 'priority' },
          {
            name: 'subject',
            filterValue: taskSubject,
            filterOperator: 'equal_to'
          },
          { name: 'due_date' },
          { name: 'task_type' },
          { name: 'is_incomplete' },
          { name: 'source' },
          { name: 'is_late' },
          { name: 'task_VF_01' }
        ]
      }
    };
    
    if (window.parent && window.parent !== window) {
      // Use specific origin instead of wildcard for security
      const targetOrigin = window.location.origin; // or specify the exact CCA domain
      window.parent.postMessage(data, targetOrigin);
    }
  };

  const formatDate = (dateString) => {
    // Handle null, undefined, or empty strings
    if (!dateString) {
      return 'N/A';
    }
    
    try {
      const date = new Date(dateString);
      
      // Check if the date is valid
      if (isNaN(date.getTime())) {
        return 'Invalid Date';
      }
      
      return date.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid Date';
    }
  };

  if (loading) {
    return <div className="text-center">Loading task details...</div>;
  }

  if (error) {
    return <div className="alert alert-danger">Error loading task details: {error}</div>;
  }

  return (
    <div className="member-task-details">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb">
        <button 
          className="breadcrumb-item breadcrumb-first btn-link"
          onClick={handleBackClick}
          style={{
            background: 'none',
            border: 'none',
            color: '#007bff',
            textDecoration: 'underline',
            cursor: 'pointer',
            padding: '0',
            fontSize: 'inherit'
          }}
        >
          <b>{loggedUserName || externalUserId}</b>
        </button>
        <span style={{ margin: '0 8px' }}>/</span>
        <div className="breadcrumb-item">
          <b>{loggedUserName || externalUserId}</b>
        </div>
      </div>

      {/* Task Details Table */}
      <div className="table-responsive">
        <table className="table table-striped">
          <thead>
            <tr>
              <th>Task ID</th>
              <th>Type of visit completed</th>
              <th>NCM</th>
              <th>Due Date</th>
              <th>Status</th>
              <th>Member Name</th>
              <th>Member Id</th>
            </tr>
          </thead>
          <tbody>
            {taskList.length === 0 ? (
              <tr>
                <td colSpan="7" className="text-center">No tasks found</td>
              </tr>
            ) : (
              taskList.map((item, index) => (
                <tr key={index}>
                  <td className="red-border">
                    <button
                      className="case-link btn-link"
                      onClick={() => handleTaskClick(item.task_Id, item.task)}
                    >
                      {item.task_Id}
                    </button>
                  </td>
                  <td>{item.task}</td>
                  <td>{item.createdBy}</td>
                  <td>{item.due_Date ? formatDate(item.due_Date) : 'N/A'}</td>
                  <td>{item.status}</td>
                  <td>{item.memberName}</td>
                  <td>{item.memberId}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default MemberTaskDetails;
